import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryContext = createContext();

// Default country is Nigeria
const DEFAULT_COUNTRY = 'NG';

export function CountryProvider({ children }) {
  const [country, setCountry] = useState(() => {
    return localStorage.getItem('cv_country') || DEFAULT_COUNTRY;
  });
  const [countryConfig, setCountryConfig] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);

  // Fetch country config immediately
  const fetchCountryConfig = useCallback(async (code) => {
    if (!code) return;
    setConfigLoading(true);
    try {
      const response = await fetch(`${API}/countries/${code}`);
      if (response.ok) {
        const data = await response.json();
        setCountryConfig(data);
      }
    } catch (error) {
      console.error('Error fetching country config:', error);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Fetch all countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API}/countries`);
        if (response.ok) {
          const data = await response.json();
          setCountries(data);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch config when country changes
  useEffect(() => {
    if (country) {
      fetchCountryConfig(country);
    }
  }, [country, fetchCountryConfig]);

  // Select country and immediately fetch config
  const selectCountry = useCallback(async (code) => {
    setCountry(code);
    localStorage.setItem('cv_country', code);
    // Immediately fetch the config for instant update
    await fetchCountryConfig(code);
  }, [fetchCountryConfig]);

  const clearCountry = useCallback(() => {
    setCountry(DEFAULT_COUNTRY);
    localStorage.setItem('cv_country', DEFAULT_COUNTRY);
    fetchCountryConfig(DEFAULT_COUNTRY);
  }, [fetchCountryConfig]);

  return (
    <CountryContext.Provider value={{ 
      country, 
      countryConfig,
      countries,
      selectCountry,
      clearCountry,
      loading,
      configLoading,
      isCountrySelected: !!country
    }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}

export default CountryContext;
